import { Controller } from '@hotwired/stimulus'
import Chart, { ChartItem } from 'chart.js/auto'
import type { Plugin } from 'chart.js'

interface Row { name: string, count: number | null }

const THRESHOLD_VALUE = 95
const POINT_GREEN = '#22C55F'
const POINT_AMBER = '#F59E0B'
const POINT_RED = '#EF4444'
const TICK_GRAY = '#6B7280'
const GRID_GRAY = 'rgba(107, 114, 128, 0.28)'
type Period = 'last_24_hours' | 'last_14_days' | 'last_30_days' | 'last_90_days'

const thresholdLinePlugin: Plugin<'line'> = {
  id: 'thresholdLine',
  beforeDatasetsDraw (chart) {
    const { ctx, chartArea, scales } = chart
    const yScale = scales.y
    if (yScale == null) return

    const y = yScale.getPixelForValue(THRESHOLD_VALUE)
    if (!Number.isFinite(y)) return

    ctx.save()
    ctx.beginPath()
    ctx.setLineDash([6, 4])
    ctx.strokeStyle = POINT_RED
    ctx.lineWidth = 1
    ctx.moveTo(chartArea.left, y)
    ctx.lineTo(chartArea.right, y)
    ctx.stroke()
    ctx.restore()
  }
}

const customGradientPlugin: Plugin<'line'> = {
  id: 'customGradient',
  afterLayout: (chart) => {
    const { ctx, scales } = chart
    const dataset = chart.data.datasets[0]
    const yScale = scales.y
    const y0 = yScale.getPixelForValue(0)
    const y100 = yScale.getPixelForValue(100)

    const gradient = ctx.createLinearGradient(0, y0, 0, y100)
    gradient.addColorStop(0, POINT_RED)
    gradient.addColorStop(0.4, POINT_AMBER)
    gradient.addColorStop(0.7, POINT_GREEN)
    gradient.addColorStop(1, POINT_GREEN)

    dataset.borderColor = gradient
    dataset.pointBackgroundColor = dataset.data.map((value) => {
      const numValue = typeof value === 'number' ? value : null

      if (numValue === null || !Number.isFinite(numValue)) {
        return TICK_GRAY
      } else if (numValue >= 70) {
        return POINT_GREEN
      } else if (numValue >= 40) {
        return POINT_AMBER
      } else {
        return POINT_RED
      }
    })
  }
}

export default class extends Controller {
  static values = {
    url: String
  }

  static targets = ['canvas', 'emptyState', 'periodSelect']

  declare urlValue: string
  declare canvasTarget: HTMLCanvasElement
  declare emptyStateTarget: HTMLElement
  declare hasEmptyStateTarget: boolean
  declare periodSelectTarget: HTMLSelectElement
  declare hasPeriodSelectTarget: boolean

  private chart?: Chart

  async connect (): Promise<void> {
    await this.render()
  }

  disconnect (): void {
    this.chart?.destroy()
    this.chart = undefined
  }

  async periodChanged (): Promise<void> {
    await this.render()
  }

  private async render (): Promise<void> {
    const period = this.selectedPeriod()
    const rows = await this.fetchRows(period)
    const hasData = rows.some((row) => typeof row.count === 'number' && Number.isFinite(row.count))
    this.toggleEmptyState(hasData)

    if (!hasData) {
      this.chart?.destroy()
      this.chart = undefined
      return
    }

    const labels = rows.map((r) => r.name)
    const values = rows.map((r) => r.count)

    const ctx = this.canvasTarget.getContext('2d')
    if (ctx == null) return

    this.chart?.destroy()

    const chart = new Chart(this.canvasTarget as ChartItem, {
      type: 'line',
      data: {
        labels,
        datasets: [
          {
            data: values,
            pointBorderWidth: 4,
            pointRadius: 1,
            pointHoverRadius: 4,
            borderWidth: 4,
            tension: 0.4,
            spanGaps: false,
            fill: false
          }
        ]
      },
      options: {
        maintainAspectRatio: false,
        scales: {
          y: {
            min: 0,
            max: 100,
            afterBuildTicks: (scale) => {
              const padding = 5
              scale.min -= padding
              scale.max += padding
            },
            ticks: {
              callback: (value) => `${value}%`,
              stepSize: 25,
              color: TICK_GRAY,
              padding: 8
            },
            grid: {
              color: GRID_GRAY
            },
            border: {
              display: false
            }
          },
          x: {
            ticks: {
              maxRotation: 0,
              autoSkip: true,
              maxTicksLimit: 6,
              includeBounds: true,
              color: TICK_GRAY
            },
            grid: {
              color: GRID_GRAY
            },
            border: {
              display: false
            }
          }
        },
        plugins: {
          legend: {
            display: false
          },
          tooltip: {
            callbacks: {
              label: (context) => `${context.parsed.y}%`
            }
          }
        }
      },
      plugins: [thresholdLinePlugin, customGradientPlugin]
    })

    this.chart = chart
  }

  private selectedPeriod (): Period {
    if (!this.hasPeriodSelectTarget) return 'last_30_days'

    return this.periodSelectTarget.value as Period
  }

  private toggleEmptyState (hasData: boolean): void {
    if (!this.hasEmptyStateTarget) return

    this.emptyStateTarget.classList.toggle('hidden', hasData)
    this.canvasTarget.classList.toggle('hidden', !hasData)
  }

  private async fetchRows (period: Period): Promise<Row[]> {
    if (this.urlValue === null || this.urlValue === '') return []

    const url = new URL(this.urlValue, window.location.origin)
    url.searchParams.set('period', period)

    const response = await fetch(url.toString(), {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin'
    })

    if (!response.ok) return []

    return await response.json()
  }
}
